import { EventEmitter, Injectable } from '@angular/core';
import { ApiServiceBase } from './api.service.base';
import { ServiceBusClient} from '@azure/service-bus'
import { OrganizationsService } from './organizations.service';
import { PermissionsService } from './permissions.service';
import { LoginServiceBase } from '../auth/login.service.base';

export enum ViolationStatus {
  New = 'New',
  UnderReview = 'UnderReview',
  ApprovedForCitation = 'ApprovedForCitation',
  Dismissed = 'Dismissed',
  CitationIssued = 'CitationIssued'
}

export interface ViolationMediaResponse {
  Id: number;
  MediaId: number;
  MediaType?: string | null;
  DisplayOrder?: number | null;
  MediaUrl?: string;
  ThumbnailUrl?: string;
}

export interface ViolationVehicleBrandResponse {
  Id: number;
  Name: string;
}

export interface ViolationVehicleModelResponse {
  Id: number;
  Name: string;
  Brand: ViolationVehicleBrandResponse;
}

export interface ViolationVehicleColorResponse {
  Id: number;
  Name: string;
  HexCode?: string | null;
}

export interface ViolationVehiclePlateResponse {
  Number: string;
  Region?: string | null;
  Country?: string | null;
}

export interface ViolationVehicleResponse {
  Plate: ViolationVehiclePlateResponse;
  Color: ViolationVehicleColorResponse;
  Model: ViolationVehicleModelResponse;
  Brand: ViolationVehicleBrandResponse;
  Year?: number | null;
}

export interface ViolationResponseModel {
  Id: number;
  ViolationStatus: string;
  Status: ViolationStatus | string;
  DetectionSource?: string | null;
  DetectionContext?: string | null;
  StatusChangedDate?: string | null;
  StatusChangedDateLocalFriendlyString?: string | null;
  LinkedCitationId?: number | null;
  ReviewedByUserId?: string | null;
  ReviewedByUserName?: string | null;
  ReviewedDate?: string | null;
  ReviewedDateLocalFriendlyString?: string | null;
  ReviewNotes?: string | null;
  VehicleId?: number | null;
  MediaCount?: number;
  ParkingSpaceName?: string | null;
  ParkingSpaceId?: string | null;
  ParkingSpaceIId?: number | null;
  ParkingSessionId?: number | null;
  ParkingSpaceLocationString?: string | null;
  ParkingSpaceGeoPolygonCenter?: number[] | null;
  ParkingLotName?: string | null;
  TicketReference?: string | null;
  PlateNumber?: string | null;
  Notes?: string | null;
  ActionedByUserId?: string | null;
  ActionedByUserName?: string | null;
  StartDateLocalFriendlyString: string;
  ActionedDateLocalFriendlyString?: string | null;
  EndDateLocalFriendlyString?: string | null;
  ParkingStartDateLocalFriendlyString?: string | null;
  HexColor?: string;
  LocationString?: string | null;
  Vehicle?: ViolationVehicleResponse | null;
  StartDate?: string;
}

export interface DetailedViolationResponseModel extends ViolationResponseModel {
  Media: ViolationMediaResponse[];
}

export interface UpdateViolationStatusRequest {
  Status: ViolationStatus;
  ReviewNotes?: string;
}

export interface AttachMediaRequest {
  MediaIds: number[];
  MediaType?: string | null;
}

export interface CreateCitationFromViolationRequest {
  Reference?: string;
  InfringementTypeIds: number[];
  IsWarning: boolean;
  Notes?: string;
  Latitude: number;
  Longitude: number;
  VehicleColorId?: number | null;
  VehicleBrandId?: number | null;
  VehicleModelId?: number | null;
  OfficerId?: number | null;
}

export interface CreateManualCitationRequest {
  Reference?: string;
  IssuedDate?: string;
  PlateNumber: string;
  VehicleBrandId?: number | null;
  VehicleModelId?: number | null;
  VehicleColorId?: number | null;
  VehicleId?: number | null;
  ParkingLotId?: string | null;
  ParkingSpaceName?: string | null;
  OfficerId: number;
  InfringementTypeIds: number[];
  IsWarning: boolean;
  Notes?: string | null;
  EmailAddress?: string | null;
  MediaIds?: number[] | null;
}

export interface InfringementTypeEscalationResponse {
  Id?: number;
  DaysAfterIssuance: number;
  AdditionalFee: number;
  Description?: string | null;
  Sequence: number;
}

export interface InfringementTypeResponse {
  Id: number;
  Name: string;
  Description?: string | null;
  BaseFine: number;
  IsActive: boolean;
  AppealEligible: boolean;
  DateCreated?: string | null;
  Escalations: InfringementTypeEscalationResponse[];
}

export interface EditInfringementTypeRequest {
  Name: string;
  Description?: string | null;
  BaseFine: number;
  IsActive: boolean;
  AppealEligible: boolean;
  Escalations?: InfringementTypeEscalationResponse[];
}

export interface OfficerResponse {
  Id: number;
  UserId: string;
  BadgeNumber?: string | null;
  IsActive: boolean;
  Notes?: string | null;
  DateCreated?: string | null;
  UserName?: string | null;
  UserEmail?: string | null;
}

export interface CreateOfficerRequest {
  CreateNewUser: boolean;
  UserId?: string | null;
  Email?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  BadgeNumber?: string | null;
  IsActive: boolean;
  Notes?: string | null;
}

export interface EditOfficerRequest {
  BadgeNumber?: string | null;
  IsActive: boolean;
  Notes?: string | null;
}

export interface OfficerStatisticsResponse {
  OfficerId: number;
  TotalCitationsIssued: number;
  CitationsThisMonth: number;
  CitationsThisWeek: number;
  CitationsPerDay: number[];
  CitationsPerDayLabels: string[];
  TotalAmountCollected: number;
  AverageCitationsPerDay: number;
}

export interface AppealMediaResponse {
  Id: number;
  MediaId: number;
  MediaType?: string | null;
  DisplayOrder?: number | null;
  MediaUrl?: string;
  ThumbnailUrl?: string;
}

export interface AppealCommunicationResponse {
  Id: number;
  SentDate: string;
  SentDateLocalFriendlyString: string;
  SenderName?: string | null;
  Message: string;
}

export interface AppealResponse {
  Id: number;
  CitationId: number;
  InfringementId: number;
  CitationReference?: string | null;
  InfringementTypeName?: string | null;
  SubmissionDate: string;
  Status: string;
  EmailAddress?: string | null;
  ReasonText: string;
  /** 0 = None, 1 = Upheld, 2 = Denied */
  Decision: number;
  DecisionName?: string | null;
  IsUpheld: boolean;
  IsDenied: boolean;
  DecisionText?: string | null;
  DecisionDate?: string | null;
  DecisionByUserId?: string | null;
  DecisionByUserName?: string | null;
  CurrentStateKey: string;
  StateEnteredDate: string;
  SubmissionDateLocalFriendlyString: string;
  DecisionDateLocalFriendlyString?: string | null;
  MediaCount: number;
  CommunicationCount: number;
}

export interface AppealNoteResponse {
  Id: number;
  AppealId: number;
  Content: string;
  CreatedDate: string;
  CreatedDateLocalFriendlyString: string;
  CreatedByUserId: string;
  CreatedByUserName?: string | null;
}

export interface AppealDetailResponse extends AppealResponse {
  Media: AppealMediaResponse[];
  DecisionMedia: AppealMediaResponse[];
  Communications: AppealCommunicationResponse[];
  Notes: AppealNoteResponse[];
}

export interface AppealActionHistoryRecord {
  Description: string;
  ActionDateLocalFriendlyString: string;
  ActionedByUserName: string;
}

export interface UpdateAppealStatusRequest {
  StateKey: string;
  DecisionText?: string | null;
}

export interface AppealDecisionRequest {
  /** 0 = None, 1 = Upheld, 2 = Denied */
  Decision: number;
  DecisionText: string;
  /** Required when Decision is Upheld */
  VoidReasonId?: number | null;

  AdjustmentAmount?: number | null;
  AdjustmentReason?: string | null;
  FreezeAfterAdjustment?: boolean | null;
  FreezeReason?: string | null;
  FreezeIndefinitely?: boolean | null;
  FreezeUntil?: Date | null;

  MediaIds: number[];
}

export interface VoidReasonResponse {
  Id: number;
  Text: string;
  Description?: string | null;
  CountsAgainstPerformance: boolean;
  PubliclyVisible: boolean;
}

export interface AddAppealCommunicationRequest {
  Message: string;
  SentDate?: string | null;
}

export interface AddCitationNoteRequest {
  Content: string;
}

export interface AddAppealNoteRequest {
  Content: string;
}

export enum CitationCommunicationDirection {
  Inbound = 0,
  Outbound = 1
}

export enum CitationCommunicationChannel {
  Email = 0,
  SystemNote = 1,
  Sms = 2,
  Phone = 3,
  PortalMessage = 4,
  PostalMail = 5
}

export enum CitationAdjustmentType {
  Discount = 0,
  Surcharge = 1
}

export interface CitationCommunicationAttachmentResponse {
  Id: number;
  CommunicationId: number;
  MediaId: number;
}

export interface CitationCommunicationResponse {
  Id: number;
  CitationId: number;
  Direction: CitationCommunicationDirection;
  Channel: CitationCommunicationChannel;
  Content: string;
  SentByUserId?: string | null;
  RecipientAddress?: string | null;
  Subject?: string | null;
  ExternalReference?: string | null;
  SentDate: string;
  SentDateLocalFriendlyString?: string | null;
  Attachments: CitationCommunicationAttachmentResponse[];
}

export enum CitationNoticeStatus {
  Pending = 0,
  Collected = 1,
  Failed = 2
}

export interface CitationNoticeResponse {
  Id: number;
  CitationId: number;
  DocumentTemplateId: string;
  DocumentTemplateName?: string | null;
  MediaId: number | null;
  NoticeType?: string | null;
  GeneratedAtUtc: string;
  GeneratedAtLocalFriendlyString?: string | null;
  Status: CitationNoticeStatus;
  StatusName?: string | null;
}

export interface CitationAdjustmentResponse {
  Id: number;
  CitationId: number;
  AdjustmentType: CitationAdjustmentType;
  Amount: number;
  Reason: string;
  Notes?: string | null;
  CreatedByUserId: string;
  CreatedByUserName?: string | null;
  CreatedDate: string;
}

export interface CitationNoteResponse {
  Id: number;
  CitationId: number;
  Content: string;
  CreatedDate: string;
  CreatedDateLocalFriendlyString: string;
  CreatedByUserId: string;
  CreatedByUserName?: string | null;
}

export interface CitationActionHistoryRecordResponse {
  Description: string;
  ActionDateLocalFriendlyString: string;
  ActionedByUserName: string;
}

export interface NoticeTriggerGroupTriggerDto {
  Id: number;
  Index: number;
  DaysAfterCreated: number;
  DocumentTemplateId?: string | null;
  ActionType: number; // 0=SendDocumentTemplate, 1=RaiseToCollections
}

export interface NoticeTriggerGroupDto {
  Id: number;
  Name: string;
  OnlyMostRecentNotice: boolean;
  EscalationProviderType: string;
  Triggers: NoticeTriggerGroupTriggerDto[];
}

export interface EscalationProviderSelectItemDto {
  Id: string;
  Name: string;
}

export interface CitationPaymentResponse {
  Id: number;
  AccountTransactionId: number;
  AmountPaid: number;
  IsRefund: boolean;
  IsManualPayment: boolean;
  TransactionReference?: string | null;
  PaymentProviderReference?: string | null;
  AmountPaidString: string;
  ReceiptURL?: string | null;
  TransactionDateLocalFriendlyString: string;
}

export interface CitationMediaResponse {
  MediaId: number;
}

export interface InfringementResponse {
  Id: number;
  CitationId: number;
  InfringementTypeId: number;
  InfringementTypeName?: string | null;
  BasePrice: number;
  CurrentPrice: number;
  IssuedDate: string;
  IsVoided: boolean;
  VoidedDate?: string | null;
  VoidedByUserId?: string | null;
  VoidReasonId?: number | null;
  VoidReasonText?: string | null;
  // Appeal information
  AppealId?: number | null;
  AppealStatus?: string | null;
  AppealSubmissionDateLocalFriendlyString?: string | null;
}

export interface CitationEscalationScheduleItem {
  EffectiveAfterDate: string;
  EffectiveAfterDateLocalFriendlyString?: string | null;
  Amount: number;
}

export interface CitationDetailResponse {
  Id: number;
  Reference: string;
  Status: string;
  TotalFeeString: string;
  IssuedDateLocalFriendlyString: string;
  IssuedByUserName?: string | null;
  PlateNumber?: string | null;
  VehicleMake?: string | null;
  VehicleColor?: string | null;
  ParkingLotId?: string | null;
  ParkingLotName?: string | null;
  ParkingSpaceName?: string | null;
  TotalFee: number;
  TotalPaid: number;
  TotalOutstanding: number;
  IsWarning: boolean;
  EmailAddress?: string | null;

  Infringements: InfringementResponse[];
  ViolationId?: number | null;
  CitationMedia: CitationMediaResponse[];
  CitationPayments: CitationPaymentResponse[];
  Communications: CitationCommunicationResponse[];
  Adjustments: CitationAdjustmentResponse[];
  EscalationSchedule: CitationEscalationScheduleItem[];
  Notes: CitationNoteResponse[];
  AvailableStatusChanges: WorkflowTransitionDto[];
  // Freeze properties
  IsFrozen: boolean;
  IsFrozenIndefinitely: boolean;
  FrozenUntil?: string | null;
  FrozenUntilLocalFriendlyString?: string | null;
  FrozenAt?: string | null;
  FrozenAtLocalFriendlyString?: string | null;
  FreezeReason?: string | null;
  FrozenByUserName?: string | null;

  // Optional DMV details (nullable)
  DMVState?: number | null;
  DMVFirstName?: string | null;
  DMVMiddleName?: string | null;
  DMVLastName?: string | null;
  DMVBirthDate?: string | null;
  DMVBirthDateFriendlyString?: string | null;

  DMVResidentialAddressLine1?: string | null;
  DMVResidentialAddressLine2?: string | null;
  DMVResidentialAddressCity?: string | null;
  DMVResidentialAddressPostCode?: string | null;

  DMVPostalAddressLine1?: string | null;
  DMVPostalAddressLine2?: string | null;
  DMVPostalAddressCity?: string | null;
  DMVPostalAddressPostCode?: string | null;

  DMVBeganProcessOnLocalFriendlyString?: string | null;
  DMVFetchedOnLocalFriendlyString?: string | null;

  FinalNoticeGeneratedOn?: string | null;
  FinalNoticeGeneratedOnLocalFriendlyString?: string | null;
  FinalNoticeDueOn?: string | null;
  FinalNoticeDueOnLocalFriendlyString?: string | null;
  FinalNoticeTag?: string | null;
}

export interface EnumDto {
  Id: number;
  Name: string;
}

export interface WorkflowTransitionDto {
  Id: number;
  Name: string;
  ToStateKey: string;
}

export interface ChangeCitationStatusRequest {
  StateKey: string;
  Reason: string;
}

export interface ApplyCitationFreezeRequest {
  Reason: string;
  FrozenUntil?: string | null;
}

export interface ClearCitationFreezeRequest {
  Reason?: string | null;
}

export interface VoidInfringementRequest {
  VoidReasonId: number;
  Comment?: string | null;
}

export interface AddCitationCommunicationRequest {
  Direction: CitationCommunicationDirection;
  Channel: CitationCommunicationChannel;
  Content: string;
  RecipientAddress?: string | null;
  Subject?: string | null;
  ExternalReference?: string | null;
  AttachmentMediaIds?: number[];
}

export interface ViolationUpdate {
  ViolationStatus: string;
  Status?: ViolationStatus | string;
  DetectionSource?: string | null;
  DetectionContext?: string | null;
  StatusChangedDate?: string | null;
  LinkedCitationId?: number | null;
  ReviewedByUserId?: string | null;
  ReviewedByUserName?: string | null;
  ReviewedDate?: string | null;
  ReviewNotes?: string | null;
  ParkingSpaceName: string;
  ParkingSpaceIId?: number | null;
  ParkingSpaceLocationString?: string | null;
  StartDateLocalFriendlyString: string;
  ActionedDateLocalFriendlyString: string | null;
  EndDateLocalFriendlyString: string | null;
  ParkingStartDateLocalFriendlyString: string | null;
  ParkingSpaceId: string | null;
  ParkingSessionId: number | null;
  TicketReference: string;
  PlateNumber: string;
  Notes: string;
  ActionedByUserId: string | null;
  Id: number;
  Selected?: boolean;
}

interface ViolationDelete {
  Id: number;
}

export interface ApplyCitationAdjustmentRequest {
  AdjustmentType: CitationAdjustmentType;
  Amount: number;
  Reason: string;
  Notes: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class EnforcementService {

  public ViolationsChanged = new EventEmitter<ViolationUpdate[]>(); // Add this line
  public CurrentConnectionString : string = "";
  private Client? : ServiceBusClient;
  public Violations: ViolationUpdate[] = [];

  //#region Violations API
  public searchViolations(filter: any) {
    return this.apiService.Post<any>("enforcement/violations/searches", filter);
  }

  public getViolation(id: number) {
    return this.apiService.Get<DetailedViolationResponseModel>(`enforcement/violations/${id}/detailed`);
  }

  public updateViolationStatus(id: number, request: UpdateViolationStatusRequest) {
    return this.apiService.Patch<ViolationResponseModel>(`enforcement/violations/${id}/status`, request);
  }

  public attachMediaToViolation(id: number, request: AttachMediaRequest) {
    return this.apiService.Post<ViolationMediaResponse[]>(`enforcement/violations/${id}/media`, request);
  }

  public getViolationMedia(id: number) {
    return this.apiService.Get<ViolationMediaResponse[]>(`enforcement/violations/${id}/media`);
  }

  public createCitationFromViolation(id: number, request: CreateCitationFromViolationRequest) {
    return this.apiService.Post<any>(`enforcement/violations/${id}/create-citation`, request);
  }

  public createViolationFromDetection(request: any) {
    return this.apiService.Post<ViolationResponseModel>(`enforcement/violations/from-detection`, request);
  }
  //#endregion

  //#region Citations
  public getCitation(id: number) {
    return this.apiService.Get<CitationDetailResponse>(`enforcement/citations/${id}`);
  }

  public getCitationActionHistory(id: number) {
    return this.apiService.Get<CitationActionHistoryRecordResponse[]>(`enforcement/citations/${id}/history`);
  }

  public changeCitationStatus(id: number, request: ChangeCitationStatusRequest) {
    return this.apiService.Post<CitationDetailResponse>(`enforcement/citations/${id}/status`, request);
  }

  public addCitationCommunication(id: number, request: AddCitationCommunicationRequest) {
    return this.apiService.Post<CitationCommunicationResponse>(`enforcement/citations/${id}/communications`, request);
  }

  public applyCitationFreeze(id: number, request: ApplyCitationFreezeRequest) {
    return this.apiService.Post<CitationDetailResponse>(`enforcement/citations/${id}/freeze`, request);
  }

  public clearCitationFreeze(id: number, request?: ClearCitationFreezeRequest) {
    return this.apiService.Delete<CitationDetailResponse>(`enforcement/citations/${id}/freeze`, request || {});
  }

  public voidInfringement(citationId: number, infringementId: number, request: VoidInfringementRequest) {
    return this.apiService.Post<CitationDetailResponse>(`enforcement/citations/${citationId}/infringements/${infringementId}/void`, request);
  }

  public addCitationNote(id: number, request: AddCitationNoteRequest) {
    return this.apiService.Post<CitationNoteResponse>(`enforcement/citations/${id}/notes`, request);
  }

  public createManualCitation(request: CreateManualCitationRequest) {
    return this.apiService.Post<CitationDetailResponse>('enforcement/citations/manual', request);
  }
  //#endregion

  //#region Notice Trigger Sets
  public searchNoticeTriggerSets(filter: any) {
    return this.apiService.Post<any>('enforcement/noticetriggersets/searches', filter);
  }

  public getNoticeTriggerSet(id: number | string) {
    return this.apiService.Get<NoticeTriggerGroupDto>(`enforcement/noticetriggersets/${id}`);
  }

  public getSelectedNoticeTriggerSet() {
    return this.apiService.Get<NoticeTriggerGroupDto>(`enforcement/noticetriggersets/selected`);
  }

  public getEscalationProviderSelectItems() {
    return this.apiService.Get<EscalationProviderSelectItemDto[]>(`enforcement/escalationproviders`);
  }

  public generateCitationNoticesByTrigger(citationIds: number[], noticeTriggerSetId: number, noticeTriggerId: number, forceNew: boolean = false) {
    return this.apiService.Post<any>(`enforcement/citationnotices/generate`, {
      CitationIds: citationIds,
      NoticeTriggerSetId: noticeTriggerSetId,
      NoticeTriggerId: noticeTriggerId,
      ForceNew: forceNew
    });
  }

  public raiseCitationsToCollectionsByTrigger(citationIds: number[], noticeTriggerSetId: number, noticeTriggerId: number) {
    return this.apiService.Post<any>(`enforcement/citationnotices/raisetocollections`, {
      CitationIds: citationIds,
      NoticeTriggerSetId: noticeTriggerSetId,
      NoticeTriggerId: noticeTriggerId,
      ForceNew: false
    });
  }

  public getCitationNoticesForCitation(citationId: number, pageSize: number = 50, subset: string = "") {
    return this.apiService.Post<any>('enforcement/citationnotices/searches', {
      PageNumber: 1,
      PageSize: pageSize,
      SearchTerm: citationId.toString(),
      Subset: subset,
      Filters: [],
      OrderByProperty: 'GeneratedAtUtc',
      Ascending: false
    });
  }

  public getAllDocumentTemplates() {
    return this.apiService.Get<any[]>('communications/documenttemplates');
  }
  //#endregion

  //#region Infringement Types
  public getInfringementTypes() {
    return this.apiService.Get<InfringementTypeResponse[]>("enforcement/infringement-types");
  }

  public searchInfringementTypes(filter: any) {
    return this.apiService.Post<any>("enforcement/infringement-types/searches", filter);
  }

  public getInfringementType(id: number) {
    return this.apiService.Get<InfringementTypeResponse>(`enforcement/infringement-types/${id}`);
  }

  public createInfringementType(request: EditInfringementTypeRequest) {
    return this.apiService.Post<InfringementTypeResponse>("enforcement/infringement-types", request);
  }

  public updateInfringementType(id: number, request: EditInfringementTypeRequest) {
    return this.apiService.Put<InfringementTypeResponse>(`enforcement/infringement-types/${id}`, request);
  }

  public deleteInfringementType(id: number) {
    return this.apiService.Delete<void>(`enforcement/infringement-types/${id}`, {});
  }
  //#endregion

  //#region Officers
  public searchOfficers(filter: any) {
    return this.apiService.Post<any>("enforcement/officers/searches", filter);
  }

  public getOfficer(id: number) {
    return this.apiService.Get<OfficerResponse>(`enforcement/officers/${id}`);
  }

  public createOfficer(request: CreateOfficerRequest) {
    return this.apiService.Post<OfficerResponse>("enforcement/officers", request);
  }

  public updateOfficer(id: number, request: EditOfficerRequest) {
    return this.apiService.Put<OfficerResponse>(`enforcement/officers/${id}`, request);
  }

  public deactivateOfficer(id: number) {
    return this.apiService.Post<void>(`enforcement/officers/${id}/deactivate`, {});
  }

  public getOfficerStatistics(id: number) {
    return this.apiService.Get<OfficerStatisticsResponse>(`enforcement/officers/${id}/statistics`);
  }
  //#endregion

  //#region Appeals
  public searchAppeals(filter: any) {
    return this.apiService.Post<any>("enforcement/appeals/searches", filter);
  }

  public getAppeal(id: number) {
    return this.apiService.Get<AppealDetailResponse>(`enforcement/appeals/${id}`);
  }

  public updateAppealStatus(id: number, request: UpdateAppealStatusRequest) {
    return this.apiService.Put<AppealResponse>(`enforcement/appeals/${id}`, request);
  }

  public decideAppeal(id: number, request: AppealDecisionRequest) {
    return this.apiService.Post<AppealResponse>(`enforcement/appeals/${id}/decide`, request);
  }

  public undoAppealDecision(id: number) {
    return this.apiService.Post<AppealResponse>(`enforcement/appeals/${id}/undo-decision`, {});
  }

  public getAppealActionHistory(id: number) {
    return this.apiService.Get<AppealActionHistoryRecord[]>(`enforcement/appeals/${id}/history`);
  }

  public getVoidReasons() {
    return this.apiService.Get<VoidReasonResponse[]>(`enforcement/voidreasons`);
  }

  public attachMediaToAppeal(id: number, mediaIds: number[]) {
    return this.apiService.Post<AppealMediaResponse[]>(`enforcement/appeals/${id}/media`, { mediaIds });
  }

  public getAppealMedia(id: number) {
    return this.apiService.Get<AppealMediaResponse[]>(`enforcement/appeals/${id}/media`);
  }

  public addAppealCommunication(id: number, request: AddAppealCommunicationRequest) {
    return this.apiService.Post<AppealCommunicationResponse>(`enforcement/appeals/${id}/communications`, request);
  }

  public addAppealNote(id: number, request: AddAppealNoteRequest) {
    return this.apiService.Post<AppealNoteResponse>(`enforcement/appeals/${id}/notes`, request);
  }
  //#endregion

  constructor(private apiService : ApiServiceBase, private organizationsService : OrganizationsService, private loginService : LoginServiceBase, private permissionService : PermissionsService) {
    this.fetchViolations();
    organizationsService.AvailableOrganizationsChanged.subscribe(result =>{
      this.ConnectServiceBus(); //connect to the bus if we aren't already connected
    });
    loginService.OrganizationChanged.subscribe(result => {
      this.ConnectServiceBus(); //disconnect from previous bus and connect to new org
    });
    this.ConnectServiceBus();
  }

  fetchViolations(){
    this.Violations = [];
    this.apiService.Get<any>("enforcement/violations/current" ).then(result => {
      this.Violations = result;
      this.Violations.sort((a, b) => {
        // Convert time strings to Date objects by adding a dummy date
        const dateA = new Date(`1970/01/01 ${a.StartDateLocalFriendlyString}`);
        const dateB = new Date(`1970/01/01 ${b.StartDateLocalFriendlyString}`);

        // Fallback if dates are invalid
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          console.warn('Invalid time found:', { a: a.StartDateLocalFriendlyString, b: b.StartDateLocalFriendlyString });
          return 0;
        }

        return dateB.getTime() - dateA.getTime();
      });

      this.ViolationsChanged.emit(this.Violations);
    });
  }

  handleViolationUpdate(message: string) {
    try {
      const violation = JSON.parse(message) as ViolationUpdate;
      console.log('New violation:', violation);
      console.log('Existing violations:', this.Violations);

      // Don't add violations that are already ended
      if (violation.EndDateLocalFriendlyString) {
        return;
      }

      // Update the violations list
      const existingIndex = this.Violations.findIndex((v) => {
        console.log('Comparing:', v.Id, violation.Id);
        return v.Id === violation.Id;
      });

      console.log('Found at index:', existingIndex);

      if (existingIndex !== -1) {
        // Update existing violation
        this.Violations[existingIndex] = violation;
      } else {
        // Add new violation
        this.Violations.push(violation);
      }

      this.Violations.sort((a, b) => {
        // Convert time strings to Date objects by adding a dummy date
        const dateA = new Date(`1970/01/01 ${a.StartDateLocalFriendlyString}`);
        const dateB = new Date(`1970/01/01 ${b.StartDateLocalFriendlyString}`);

        // Fallback if dates are invalid
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          console.warn('Invalid time found:', { a: a.StartDateLocalFriendlyString, b: b.StartDateLocalFriendlyString });
          return 0;
        }

        return dateB.getTime() - dateA.getTime();
      });

      this.ViolationsChanged.emit(this.Violations);

    } catch (error) {
      console.error('Error parsing violation update:', error);
    }
  }

  handleViolationDelete(message: string) {
    try {
      const deleteInfo = JSON.parse(message) as ViolationDelete;
      // Remove the violation from the list
      this.Violations = this.Violations.filter((v) => v.Id !== deleteInfo.Id);
      this.ViolationsChanged.emit(this.Violations);
    } catch (error) {
      console.error('Error parsing violation deletion:', error);
    }
  }

  getViolationStatusColor(status: string): string {
    switch(status) {
      case ViolationStatus.New:
      case 'Pending':
        return '#D32F2F';
      case ViolationStatus.UnderReview:
        return '#D48F2F';
      case ViolationStatus.ApprovedForCitation:
      case 'Actioned':
        return '#2E7D32';
      case ViolationStatus.CitationIssued:
        return '#283593';
      case ViolationStatus.Dismissed:
      case 'Actioned and Vacated':
      case 'Vacated without Action':
        return '#9E9E9E';
      default:
        return '#000000';
    }
  }

  getViolationStatuses(): string[] {
    return [
      ViolationStatus.New,
      ViolationStatus.UnderReview,
      ViolationStatus.ApprovedForCitation,
      ViolationStatus.Dismissed,
      ViolationStatus.CitationIssued
    ];
  }

  private ConnectServiceBus(){
    this.organizationsService.GetServiceBusListenerConnectionString().then((x: any) => {
      if(this.CurrentConnectionString == x){
        //same connection as before
        return;
      }
      if(this.Client != null){
        //if we were connected before then close
        this.Client.close();
      }
      if(x == null || x == ''){
        return; //can't connect with no connecton string
      }

     this.apiService.Post<any>("enforcement/servicebus/topics/Enforcement/" + this.loginService.UserId() + this.permissionService.GetSessionIdentifier(), {}).then(result => {
      this.Client = new ServiceBusClient(x);
      const receiver = this.Client.createReceiver("Enforcement", this.loginService.UserId() + this.permissionService.GetSessionIdentifier());
      const SBMessageHandler = async (messageReceived:any) => {
        const messageType = messageReceived.applicationProperties?.MessageType;
        const messageBody = messageReceived.body;

        switch (messageType) {
          case 'ViolationCreated':
            this.handleViolationUpdate(JSON.stringify(messageBody));
            break;
          case 'ViolationDeleted':
            this.handleViolationDelete(JSON.stringify(messageBody));
            break;
          case 'ViolationUpdated':
            this.handleViolationUpdate(JSON.stringify(messageBody));
            break;
        }
      };
      const SBErrorHandler = async (error:any) => {
        console.log(error);
      };
      receiver.subscribe({
        processMessage: SBMessageHandler,
        processError: SBErrorHandler
      });
     });
    });
  }

  public RemoveViolation(violationId: number) {
    // Remove from local array
    const index = this.Violations.findIndex(v => v.Id === violationId);
    if (index > -1) {
      this.Violations.splice(index, 1);
      // Emit change event
      this.ViolationsChanged.emit(this.Violations);
    }
  }

  public RefetchData() {
    this.fetchViolations();
    this.ConnectServiceBus();
  }

  // --- DMV (additions at bottom to minimize diffs) ---
  public GetConfiguredDmvProvider(): Promise<ConfiguredDmvProviderResponse> {
    return this.apiService.Get<ConfiguredDmvProviderResponse>("enforcement/dmv/provider/configured");
  }

  public ProcessPendingDmvRecords(citationIds: number[]): Promise<DmvProcessDmvRequestsResult> {
    return this.apiService.Post<DmvProcessDmvRequestsResult>("enforcement/dmv/pending/process", { CitationIds: citationIds ?? [] });
  }

  public ImportDmvResults(request: DmvManualImportRequest): Promise<DmvManualImportResult> {
    return this.apiService.Post<DmvManualImportResult>("enforcement/dmv/import", request);
  }

  // --- Collections (additions at bottom to minimize diffs) ---
  public GetConfiguredCollectionsProvider(): Promise<ConfiguredCollectionsProviderResponse> {
    return this.apiService.Get<ConfiguredCollectionsProviderResponse>("enforcement/collections/provider/configured");
  }

  public ProcessMissingCollectionsRecords(citationIds: number[]): Promise<CollectionsExportResult> {
    return this.apiService.Post<CollectionsExportResult>("enforcement/collections/missing/process", { CitationIds: citationIds ?? [] });
  }

  public ImportCollectionsResults(request: CollectionsImportRequest): Promise<CollectionsImportResult> {
    return this.apiService.Post<CollectionsImportResult>("enforcement/collections/import", request);
  }

  public ApplyCitationAdjustment(citationId: number, request: ApplyCitationAdjustmentRequest) {
    return this.apiService.Post<CitationAdjustmentResponse>(`enforcement/citations/${citationId}/adjustments`, request);
  }
}

// --- DMV types (additions at bottom to minimize diffs) ---
export interface ConfiguredDmvProviderResponse {
  IsConfigured: boolean;
  ProviderId?: string | null;
  ProviderName?: string | null;
  Method: number;
  AcceptsManualUploads: boolean;
  UIButtonText?: string | null;
}

export interface DmvProcessDmvRequestsResult {
  Results: any[];
  ExportFile?: DmvManualExport | null;
}

export interface DmvManualExport {
  FileName: string;
  ContentType: string;
  Bytes: string | number[];
}

export interface DmvManualImportRequest {
  FileName: string;
  ContentType: string;
  Bytes: string; // base64
}

export interface DmvManualImportResult {
  Results: any[];
  Warnings: string[];
  LinesProcessed: number;
  CitationsUpdated: number;
  ProcessingCitationsNotUpdated: number;
  UploadRowsNotLinkedToProcessingCitation: number;
}

// --- Collections types ---
export interface ConfiguredCollectionsProviderResponse {
  IsConfigured: boolean;
  ProviderId?: string | null;
  ProviderName?: string | null;
  Method: number;
  AcceptsManualUploads: boolean;
  UIButtonText?: string | null;
  UploadUIButtonText?: string | null;
  UploadAllowedExtensions?: string[] | null;
}

export interface CollectionsExportResult {
  ExportFile?: CollectionsExportFile | null;
}

export interface CollectionsExportFile {
  FileName: string;
  ContentType: string;
  Bytes: string | number[];
}

export interface CollectionsImportRequest {
  FileName: string;
  ContentType: string;
  Bytes: string; // base64
}

export interface CollectionsImportResult {
  LinesProcessed: number;
  PaymentsApplied: number;
  Warnings: string[];
  Rows?: any[];
}
